import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AssignmentDetailPanel = ({ driver, onClose, onCreateAssignment }) => {
  if (!driver) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-1 h-full flex items-center justify-center">
        <div className="text-center px-6 py-12">
          <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-full mx-auto mb-4">
            <Icon name="UserCircle" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-2">No Driver Selected</h3>
          <p className="text-xs text-muted-foreground">
            Select a driver from the table to view assignment details and history
          </p>
        </div>
      </div>
    );
  }

  const currentAssignments = [
    {
      id: 1,
      type: 'Primary',
      unitNumber: 'UNIT-2847',
      vin: '1HGBH41JXMN109186',
      startDate: '2025-01-15',
      status: 'Active',
      yardLocation: 'Phoenix Main Yard'
    }
  ];

  const assignmentHistory = [
    {
      id: 1,
      type: 'Primary',
      unitNumber: 'UNIT-1923',
      vin: '1HGBH41JXMN109145',
      startDate: '2024-06-10',
      endDate: '2025-01-14',
      duration: '7 months',
      yardLocation: 'Tucson Distribution'
    },
    {
      id: 2,
      type: 'Relief',
      unitNumber: 'UNIT-3456',
      vin: '1HGBH41JXMN109167',
      startDate: '2024-03-20',
      endDate: '2024-06-09',
      duration: '3 months',
      yardLocation: 'Phoenix Main Yard'
    },
    {
      id: 3,
      type: 'Primary',
      unitNumber: 'UNIT-0892',
      vin: '1HGBH41JXMN109123',
      startDate: '2023-11-05',
      endDate: '2024-03-19',
      duration: '4 months',
      yardLocation: 'Mesa Service Center'
    }
  ];

  const getAssignmentTypeColor = (type) => {
    return type === 'Primary' ?'text-primary bg-primary/10' :'text-secondary bg-secondary/10';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 h-full flex flex-col">
      <div className="sticky top-0 bg-card border-b border-border px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Assignment Details</h3>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-hover"
          >
            <Icon name="X" size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          <div className="flex items-start space-x-4 pb-4 border-b border-border">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={driver?.avatar}
                alt={driver?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-base font-semibold text-foreground mb-1">{driver?.name}</h4>
              <p className="text-sm text-muted-foreground mb-2">{driver?.driverId}</p>
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Phone" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="text-xs text-muted-foreground font-mono">{driver?.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${driver?.isActive ? 'bg-success' : 'bg-muted-foreground'}`}></div>
                <span className="text-xs text-muted-foreground">{driver?.isActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold text-foreground">Current Assignments</h5>
              <Button
                variant="outline"
                size="xs"
                iconName="Plus"
                onClick={() => onCreateAssignment(driver)}
              >
                New
              </Button>
            </div>
            {currentAssignments?.length > 0 ? (
              <div className="space-y-3">
                {currentAssignments?.map((assignment) => (
                  <div key={assignment?.id} className="bg-muted/30 rounded-lg p-3 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getAssignmentTypeColor(assignment?.type)}`}>
                        {assignment?.type} Driver
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-success bg-success/10">
                        {assignment?.status}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="Truck" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-sm font-medium text-foreground">{assignment?.unitNumber}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Hash" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-xs text-muted-foreground font-mono">{assignment?.vin}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-xs text-muted-foreground">{assignment?.yardLocation}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-xs text-muted-foreground">Since {assignment?.startDate}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-muted/30 rounded-lg border border-dashed border-border">
                <Icon name="AlertCircle" size={24} color="var(--color-muted-foreground)" strokeWidth={2} className="mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No active assignments</p>
              </div>
            )}
          </div>

          <div>
            <h5 className="text-sm font-semibold text-foreground mb-3">Assignment History</h5>
            <div className="space-y-3">
              {assignmentHistory?.map((assignment, index) => (
                <div key={assignment?.id} className="relative pl-6 pb-3">
                  <div className="absolute left-0 top-1.5 w-3 h-3 bg-muted border-2 border-border rounded-full"></div>
                  {index < assignmentHistory?.length - 1 && (
                    <div className="absolute left-[5px] top-5 bottom-0 w-0.5 bg-border"></div>
                  )}
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getAssignmentTypeColor(assignment?.type)}`}>
                        {assignment?.type}
                      </span>
                      <span className="text-xs text-muted-foreground">{assignment?.duration}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center space-x-2">
                        <Icon name="Truck" size={12} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-xs font-medium text-foreground">{assignment?.unitNumber}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" strokeWidth={2} />
                        <span className="text-xs text-muted-foreground">{assignment?.yardLocation}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <span>{assignment?.startDate}</span>
                        <Icon name="ArrowRight" size={12} strokeWidth={2} />
                        <span>{assignment?.endDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} color="var(--color-primary)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-primary mb-1">Compliance Status</p>
                <p className="text-xs text-muted-foreground">
                  All assignment records are maintained for audit compliance. Historical data is preserved for regulatory reporting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetailPanel;